import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import WithdrawalService from "../services/manager/withdrawalService";
import { Box, Button, Container, CssBaseline, Grid, InputAdornment, Pagination, Snackbar, SnackbarContent, TextField, Typography } from "@mui/material";
import AdminNavigation from "./adminNavigation";
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { btnStyleContrast, btnStyleOutlined, lightergrayHex, paginationContrast, headerStyleContrast, subHeaderStyleContrast, textStyleContrast, textStyleLargeContrast } from "../helper/style";
import noImage from "../assets/no-image.png";
import { NumericFormat } from "react-number-format";
import LiqueModal from "../customer/components/liqueModal";
import dayjs from "dayjs";
import OrderService from "../services/manager/orderService";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PartnerTransactionPDF from "./partnerTransactionPDF";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const imageUrl = process.env.REACT_APP_IMAGE_URL

const RootContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Withdrawal = () => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [msgSuccess, setmsgSuccess] = useState("");
  const [showError, setShowError] = useState(false);
  const [msgError, setMsgError] = useState();
  const [openAdd, setOpenAdd] = useState(false);
  
  const [listPartner, setListPartner] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [periodStart, setPeriodStart] = useState(dayjs());
  const [periodEnd, setPeriodEnd] = useState(dayjs());
  const [period, setPeriod] = useState(dayjs);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItem, setTotalItem] = useState(0);
  const [totalPage, setTotalPage] = useState(1);

  const [loading, setLoading] = useState(false);
  
  const [openPrint, setOpenPrint] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [loadingPrint, setLoadingPrint] = useState(false);
  const [generateInvoiceDate, setGenerateInvoiceDate] = useState(new Date());
  const [fileName, setFileName] = useState(null);

  const activateSuccess = (msg) => {
    setmsgSuccess(msg);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
    }, 5000);
  };

  const activateError = (msg) => {
    setMsgError(msg)
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
    }, 5000);
  };

  const handleOpenAdd = (partner) => {
    setSelectedPartner(partner);
    setOpenAdd(true);
  }

  const handleCloseAdd = () => {
    setOpenAdd(false);
    setSelectedPartner(null);
  }

  const handleOpenPrint = (partner) => {
    setSelectedPartner(partner);
    setOpenPrint(true);

    fetchPrintData(partner);
  }

  const handleClosePrint = () => {
    setOpenPrint(false);
    setSelectedPartner(null);
  }

  const getListPartner = async () => {
    if (!period) {
      activateError("Silahkan pilih periode terlebih dahulu");
      return;
    }

    try {
      const response = await WithdrawalService.getListWithdrawal(page, perPage, search, period.format("YYYY-MM-DD"));
      if (response) {
        const data = response.data;
        setListPartner(data.partnerList);
        setTotalItem(data.totalItem);
        setTotalPage(data.totalPage);
        setPeriodStart(dayjs(data.withdrawPeriodStart));
        setPeriodEnd(dayjs(data.withdrawPeriodEnd));
      }
    } catch (error) {
      console.error("Error fetching partner list:", error);
      activateError("Gagal mendapatkan data mitra");
    }
  }

  const addWithdrawal = async () => {
    if (selectedPartner == null) {
      activateError("Gagal menarik dana");
      return;
    }

    if (!period) {
      activateError("Silahkan pilih periode terlebih dahulu");
      return;
    }

    try {
      const response = await WithdrawalService.addWithdrawal(selectedPartner.id, period.format("YYYY-MM-DD"));
      if (response) {
        activateSuccess("Berhasil menarik dana");
        getListPartner();
        handleCloseAdd();
      }
    } catch (error) {
      console.error("Error add withdrawal:", error);
      if (error.response.status === 400) {
        const data = error.response.data;
        activateError(data.message);
      } else {
        activateError("Gagal menarik dana");
      }
    } finally {
      setLoading(false);
    }
  }

  const fetchPrintData = async (partnerArg) => {
    try {
      setLoadingPrint(true);
      const partner = partnerArg || selectedPartner;
      const response = await OrderService.getListTransactionsPrint(
        partner?.id,
        periodStart ? periodStart.format("YYYY-MM-DD") : null,
        periodEnd ? periodEnd.format("YYYY-MM-DD") : null
      );
      if (response) {
        const data = response.data;
        setGenerateInvoiceDate(new Date());
        setPrintData(data);
      }
    } catch (error) {
      activateError("Gagal mendapatkan pesanan")
    } finally {
      setLoadingPrint(false);
    }
  };

  useEffect(() => {
    if (period) {
      getListPartner();
    }
  }, []);
  
  const searchTimeout = useRef(null);
  useEffect(() => {
    if (period) {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
  
      searchTimeout.current = setTimeout(() => {
        setPage(1);
        getListPartner();
      }, 500);

      return () => clearTimeout(searchTimeout.current);
    }
  }, [search, period]);
  
  useEffect(() => {
    if (period) {
      getListPartner();
      window.scrollTo(0, 0);
    }
  }, [page]);

  useEffect(() => {
    if (printData) {
      setFileName(
        `${dayjs(generateInvoiceDate).format("YYYYMMDD")}_${printData?.partner?.name?.toUpperCase()?.replaceAll(" ", "-")}_INVOICE.pdf`
      );
    }
  }, [generateInvoiceDate, printData]);

  return (
    <Box>
      <CssBaseline />
      <AdminNavigation />

      <Container
        component="main"
        maxWidth="xl"
      >
        <RootContainer>
          <Snackbar
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            open={showSuccess}
            autoHideDuration={3000}
            onClose={() => setShowSuccess(false)}
          >
            <SnackbarContent
              sx={{ backgroundColor: "#19B600" }} 
              message={msgSuccess}/>
          </Snackbar>
    
          <Snackbar
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            open={showError}
            autoHideDuration={3000}
            onClose={() => setShowError(false)}
          >
            <SnackbarContent 
              sx={{ backgroundColor: "#E50B0B" }} 
              message={msgError}/>
          </Snackbar>


          <Grid container spacing={1} alignItems="center" marginBottom={2}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ ...headerStyleContrast }}>
                Penarikan Dana
              </Typography>
            </Grid>
            <Grid item xs={6} md={6}>
              <TextField
                fullWidth
                value={search}
                placeholder="Cari Mitra..."
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ 
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Typography>Periode</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-sg">
                <DatePicker
                  sx={{ width: "100%" }}
                  value={period}
                  onChange={(newValue) => setPeriod(newValue)}
                  renderInput={(params) => <TextField fullWidth {...params} />}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          <Box sx={{ marginTop: "20px", width: "100%" }} marginBottom={3}>
            <Box marginBottom={2}>
              <Typography sx={{ ...textStyleLargeContrast }}>
                Periode {periodStart.format("DD MMM YYYY")} - {periodEnd.format("DD MMM YYYY")}
              </Typography>
            </Box>
            {
              listPartner.map(partner => (
                <Box  
                  key={partner.id}
                  sx={{ background: "white", borderRadius: "16px", padding: "16px", marginBottom: "20px", boxShadow: "2px 2px 10px rgba(0, 0, 0, .2)" }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} display="flex" gap="20px" alignItems="center">
                      <Typography sx={{ ...textStyleContrast, color: lightergrayHex }} display="flex" alignItems="center" gap="10px">
                        <CalendarTodayOutlinedIcon /> <span>Last Withdrawal: {partner.lastUpdate || '-'}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={4} md={1}>
                      <Box sx={{ width: "100%" }}>
                        <img
                          src={
                            !partner.logo
                            ? noImage
                            : `${imageUrl}${partner.logo}`
                          }
                          alt={`Logo ${partner.name}`}
                          style={{
                            width: "100%",
                            aspectRatio: "1 / 1",
                            objectFit: "cover",
                            marginRight: "3%",
                            borderRadius: "8px"
                          }}
                        ></img>
                      </Box>
                    </Grid>
                    <Grid item xs={8} md={5}>
                      <Typography sx={{ ...subHeaderStyleContrast, fontWeight: "500" }}>
                        {partner.name}
                      </Typography>
                      <Typography sx={{ ...textStyleContrast, color: lightergrayHex }}>
                        Sisa Saldo:&nbsp;
                        <NumericFormat
                          value={partner.balance}
                          displayType={"text"}
                          thousandSeparator="."
                          decimalSeparator=","
                          prefix={"Rp. "}
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6} display="flex" justifyContent="end" alignItems="end" gap="10px">
                      <Button 
                        sx={{ ...btnStyleContrast, borderRadius: "50px", width: "120px" }}
                        onClick={() => handleOpenPrint(partner)}
                        startIcon={<FileDownloadOutlinedIcon sx={{ color: "white" }} />}
                      >
                        Invoice
                      </Button>
                      <Button 
                        sx={{ ...btnStyleContrast, borderRadius: "50px", width: "120px" }}
                        onClick={() => handleOpenAdd(partner)}
                        disabled={(partner.balance || 0) <= 0}
                      >
                        Tarik Saldo
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              ))
            }

            <Box display="flex" justifyContent="center">
              <Pagination sx={paginationContrast} 
                size="small"
                count={totalPage} 
                page={page}
                onChange={(_event, page) => setPage(page)} />
            </Box>
          </Box>
        </RootContainer>
      </Container>

      <LiqueModal open={openAdd} onClose={handleCloseAdd} width="700px">
        <Box textAlign="center">
          <Typography id="spring-modal-title" variant="h6" component="h2">
            Apakah kamu yakin ingin menarik dana untuk partner <b>{selectedPartner?.name || '-'}</b> sejumlah&nbsp;
            <b>
              <NumericFormat
                value={selectedPartner?.balance || 0}
                displayType={"text"}
                thousandSeparator="."
                decimalSeparator=","
                prefix={"Rp. "}
              />
            </b>
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={handleCloseAdd} sx={{ ...btnStyleOutlined, color: "black", borderRadius: "50px", width: "120px" }}>
              Batal
            </Button>
            <Button
              variant="outlined"
              onClick={addWithdrawal}
              sx={{ ml: 2, ...btnStyleContrast, borderRadius: "50px", width: "120px" }}
              disabled={loading}
            >
              {loading ? 'Mohon menunggu...' : 'Tarik Dana'}
            </Button>
          </Box>
        </Box>
      </LiqueModal>

      <LiqueModal open={openPrint} onClose={handleClosePrint} width="500px">
        <Box textAlign="ccenter">
          {
            printData != null && (
              <PDFDownloadLink
                style={{ width: "100%" }}
                document={
                  <PartnerTransactionPDF
                    transactionData={printData} 
                    dateFrom={periodStart}
                    dateTo={periodEnd}
                    isFromWithdrawal
                  />
                }
                fileName={fileName}
              >
                {({loading}) => (
                  <Button
                    sx={{ ...btnStyleContrast, borderRadius: "50px", width: "100%" }} 
                    disabled={loading}
                    startIcon={<FileDownloadOutlinedIcon sx={{ color: "white" }} />}
                  >
                    {loading ? "Mohon Menunggu..." : "Cetak Invoice"}
                  </Button>
                )}
              </PDFDownloadLink>
            )
          }
        </Box>
      </LiqueModal>
    </Box>
  )
}

export default Withdrawal;